/**
 * PoweredByOlytic — Salesforce-style attribution.
 *
 * Push 72 (2026-07-17). Extracted from FooterChrome and promoted to a
 * standalone primitive so it can mount anywhere — including inside the
 * authenticated shells where Round 14 Item B (2026-06-25) deliberately
 * dropped the full FooterChrome mount.
 *
 * The pattern is the same as Salesforce Communities: a customer can
 * rebrand their portal end-to-end, but a small "Powered by Salesforce"
 * pill stays visible so platform attribution survives the rebrand. Ean's
 * 2026-07-17 directive: "we keep the subtle shoutout to olytic by having
 * subtle calls to olytic or whatever similar to how salesforce lets
 * people rebrand their portal but you still have the url, and maybe
 * subtle 'powered by olytic' or something similar that shows up."
 *
 * Visual grammar:
 *   - Muted grey text (baked hex — NOT CSS-var — so a workspace with
 *     brand overrides still sees the attribution in unbranded Olytic
 *     grey; the whole point is that the platform nod survives the
 *     rebrand).
 *   - Olytic mark loaded from /olytic-mark.png (<img>-loaded
 *     so its embedded stroke colors paint verbatim; CSS custom-property
 *     cascades don't reach across an <img> boundary).
 *   - Small (11-12px), unobtrusive.
 *   - Opens olytic.com in a new tab.
 *
 * Two variants:
 *   - 'inline' — flows inline with surrounding text (used inside
 *     FooterChrome and any explicit legal footer). Displays as
 *     "Powered by Olytic" with the mark between words.
 *   - 'corner' — a floating chip anchored to the bottom-RIGHT of the
 *     viewport, sticker-style. Used in the authenticated shells
 *     (UserShell / AdminShell) — mount is already gated on the
 *     workspace having a custom brand logo, so this variant only
 *     appears in the branded case. Auto-hides on narrow viewports
 *     where every pixel of bottom real estate is precious (mobile
 *     chat / compose).
 *
 * Sub-agent FFFFF Track 2 (2026-07-29) — restyle pass:
 *   - Unified font: "Powered by" and "Olytic" now share weight (500)
 *     and size (11px) so both words feel equal-weighted (prior version
 *     had "Powered by" at 400 which read thinner).
 *   - Subtle brand accent: hairline teal border on the pill (~14%
 *     opacity teal border-color) + the three-arc mark keeps its
 *     embedded brand hues. No underline, no filled tint — the accent
 *     is JUST the whisper of teal at the pill edge.
 *   - Backdrop blur retained (8px, with the standard fallback to a
 *     solid 92% white background where blur isn't supported).
 *   - Hover: opacity fade to 75% for the elegant iOS-DNA feel. No
 *     tilt / scale — hover should stay quiet since the chip itself is
 *     footer chrome.
 *
 * Sub-agent KKKKK Track 4 (2026-07-29) — position + sticker delta:
 *   - Corner variant moved from bottom-CENTER (FFFFF) back to bottom-
 *     RIGHT per Ean directive: "Powered by Olytic should be fixed in
 *     the bottom right floating on top of everything like a sticker
 *     (when personally branded in workspace)." The primitive is only
 *     mounted when a custom brand is present (UserShell + AdminShell
 *     both gate on brand_logo_r2_key), so bottom-right applies to
 *     every render.
 *   - Padding bumped 4×12 → 6×12 + shadow strengthened 0 1px 2px 4%
 *     → 0 2px 8px 12% for the "sticker" feel (small elevation off the
 *     canvas).
 */

const ATTRIBUTION_GREY = 'rgb(115, 128, 143)';
const ATTRIBUTION_GREY_HOVER = 'rgb(88, 100, 115)';

interface PoweredByOlyticProps {
  /** 'inline' flows with text; 'corner' floats at the viewport bottom-right
   *  (sticker style — Sub-agent KKKKK Track 4, 2026-07-29). */
  variant?: 'inline' | 'corner';
}

export function PoweredByOlytic({ variant = 'inline' }: PoweredByOlyticProps) {
  // Sub-agent FFFFF Track 2 (2026-07-29) — unified font weight (500) so
  // "Powered by" and "Olytic" carry equal typographic weight. Prior
  // implementation used 400 for the anchor + 600 for the Olytic word,
  // which read visually mismatched. Kept the wordmark span so the anchor
  // structure survives, but style-wise both words are now identical.
  const inline = (
    <a
      href="https://www.olytic.com"
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        color: ATTRIBUTION_GREY,
        textDecoration: 'none',
        fontFamily: 'inherit',
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: '0.02em',
        transition: 'color 120ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = ATTRIBUTION_GREY_HOVER;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = ATTRIBUTION_GREY;
      }}
      aria-label="Powered by Olytic — opens olytic.com in a new tab"
    >
      <span>Powered by</span>
      <img
        src="/olytic-mark.png"
        alt=""
        aria-hidden="true"
        width={12}
        height={12}
        style={{ display: 'inline-block', verticalAlign: 'middle' }}
      />
      <span>Olytic</span>
    </a>
  );

  if (variant === 'inline') return inline;

  // Sub-agent KKKKK Track 4 (2026-07-29) — floating chip, bottom-RIGHT
  // sticker. Anchored to the viewport corner (12px inset) so it feels
  // like a stamp on the page, not part of the shell chrome. The mount
  // is already gated on custom-brand workspaces (UserShell +
  // AdminShell both gate on brand_logo_r2_key), so this variant only
  // appears in the branded case.
  //
  // `pointer-events: none` on the wrapper keeps the chip from
  // intercepting hovers on the composer / chat rails underneath; the
  // anchor itself flips back to `pointer-events: auto` so the link is
  // clickable.
  //
  // Backdrop blur (8px) with a solid 92% white fallback for browsers
  // that don't support `backdrop-filter`. Hairline teal border (~14%
  // opacity teal) is the subtle brand accent Ean asked for — quiet at
  // rest, still visibly "Olytic" if you look at it. Padding + shadow
  // bumped from the FFFFF center-variant defaults for the "sticker"
  // feel.
  return (
    <div
      className="powered-by-olytic-corner"
      style={{
        position: 'fixed',
        right: 12,
        bottom: 12,
        zIndex: 40,
        pointerEvents: 'none',
      }}
      aria-hidden={false}
    >
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border:
            '1px solid color-mix(in srgb, var(--olytic-teal, #0F8488) 14%, rgba(15, 23, 42, 0.06))',
          borderRadius: 999,
          padding: '6px 12px',
          boxShadow: '0 2px 8px rgba(15, 23, 42, 0.12)',
          pointerEvents: 'auto',
          transition: 'opacity 200ms ease',
          userSelect: 'none',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '0.75';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '1';
        }}
      >
        {inline}
      </div>
    </div>
  );
}
